<template>
	<SpacebarUnmute>
		<div
			class="meeting-container"
			ref="container"
			@dragenter.prevent="dragChange(true, $event)"
			@dragover.prevent="dragChange(true)"
			@dragleave.prevent="dragOff"
			@drop.prevent="dragChange(false)"
		>
			<MeetingEnded v-if="meetingEnded" :endreason="endReason" />

			<JoinMeetingPopup
				v-if="showJoinMeetingPopup && notPublishing && !meetingEnded"
				@broadcast="startPublishing"
				@close="showJoinMeetingPopup = false"
			/>

			<div v-if="!isConnected" class="meeting-spinner">
				<IosSpinner
					background="#111"
					spinnerColor="#ffffff"
					class="connecting-spinner"
				/>
			</div>

			<AudioBlockedWarning
				class="meeting-banner"
				:show="showAudioBlockedWarning"
			/>

			<div
				class="meeting-layout"
				:class="{ 'with-captions': isCaptionsActive }"
				v-if="isConnected && !meetingEnded"
				@drop.prevent
				tabindex="-1"
			>
				<Header
					:sessiondata="sessionData"
					class="meeting-header"
					:view="view"
					:isSidebarOpen="isSidebarOpen"
					:isFullscreen="fullscreenActive"
					@set-sidebar-open="setSidebarOpen"
					@change-view="changeView"
					@fullscreen="toggleFullscreen"
					aria-hidden="true"
				/>

				<Streams
					class="meeting-main"
					:view="view"
					:sessiondata="sessionData"
					@change-view="changeView"
				/>

				<SidebarPlaceholder class="meeting-sidebar" :isOpen="isSidebarOpen" />

				<Captions />
			</div>

			<ErrorModal
				v-if="showError"
				:error="errorData"
				@close="showError = false"
			/>

			<MeetingLimitPopup
				v-if="showOverLimitPopup"
				@close="showOverLimitPopup = false"
			/>

			<ProfileCard
				v-if="profileData"
				@close="profileData = null"
				@error="profileError"
				:profileData="profileData"
				hideMessage
			/>

			<MediaModal />
		</div>
	</SpacebarUnmute>
</template>

<script>
import Header from './Header'
import Streams, { views } from './Streams'
import OT from '@opentok/client'
import screenfull from 'screenfull'
import ErrorModal from './ErrorModal'
import { showAlert, hideAlert } from './MeetingAlert'
import { MicOffIcon } from '@/icons'
import { SidebarPlaceholder } from './Sidebar'
import IosSpinner from '../IosSpinner'
import MeetingEnded from './MeetingEnded'
import JoinMeetingPopup from './JoinMeetingPopup'
import MeetingLimitPopup from './MeetingLimitPopup'
import { fetchMyMembershipForChat } from '@/api/chat'
import {
	initPusher,
	subscribeToPusherChannels,
	joinStream,
	preStream,
	store,
	setConnection,
	removeConnection,
	setStream,
	removeStream,
	setVideoSession,
	getIsPublishing,
	setPublishing,
	getIsSharing,
	setSharing,
	getScreenPublisher,
	getPublisher,
	removePublisher,
	removeScreenPublisher,
	getErrorText,
	setSession,
	getSession,
	hasConnection,
	getMyConnectionId,
	setUserPrefs,
	stopVideoSession,
	stopStream,
	getUserByConnectionId,
	hasSubscribers,
	isOverLimit,
	logMeetingEvent,
	getBubble,
} from './data'
import AudioBlockedWarning from './AudioBlockedWarning'
import SpacebarUnmute from './SpacebarUnmute'
import ProfileCard from '../ProfileCard'
import mainStore, {
	getMyMembership,
	getOrganization,
	normalizeChat,
} from '@/store'
import { MediaModal } from '@/components/common'
import { switchGlobalColorScheme } from '@/helpers/colors'
import { colorSchemes } from '@/helpers/constants'
import Captions from './Captions'
import i18n from '@/i18n'
import { subscribeToPresence, unsubscribeToPresence } from '@/helpers/pusher'

const FIVE_MINS = 300000
let dragTarget = null

export default {
	name: 'Meeting',
	components: {
		Streams,
		Header,
		ErrorModal,
		SidebarPlaceholder,
		IosSpinner,
		AudioBlockedWarning,
		MeetingEnded,
		JoinMeetingPopup,
		SpacebarUnmute,
		MeetingLimitPopup,
		ProfileCard,
		MediaModal,
		Captions,
	},
	data() {
		const bubbleId = this.parseBubbleIdFromRoute()
		return {
			store,
			bubbleId,
			sessionData: {
				sessionId: null,
				apiKey: null,
				token: null,
				bubble_id: '',
				videosession_id: null,
			},
			i18n,
			isReady: i18n.loaded || false,
			showRecordingAlert: false,
			view: views.GALLERY,
			isSidebarOpen: true,
			showError: false,
			showJoinMeetingPopup: false,
			errorData: {},
			meetingEnded: false,
			endReason: 'meeting_ended',
			showAudioBlockedWarning: false,
			recordingAlert: null,
			showOverLimitPopup: false,
			initialized: false,
			offlineAlert: null,
			fullscreenActive: false,
			profileData: false,
			isCaptionsActive: false,
		}
	},

	async created() {
		logMeetingEvent('#### ********* STARTING MEETING ************ ####')

		// meetings should always be in the dark scheme
		switchGlobalColorScheme(colorSchemes.DARK)

		window.addEventListener('offline', () => {
			if (this.offlineAlert) {
				hideAlert(this.offlineAlert)
			}

			this.offlineAlert = showAlert({
				text: this.$t('meetings_live_error_droppedconnection'),
				autoHide: false,
			})
		})

		window.addEventListener('online', () => {
			if (this.offlineAlert) {
				hideAlert(this.offlineAlert)
			}
		})

		this.bubbleId = this.parseBubbleIdFromRoute()
		this.readUserPrefsFromRoute()
		this.$onEventBus({
			'i18n-loaded': () => {
				this.isReady = true
			},
			stopPublishing: (event) => {
				console.debug('stopPublishing Event')
				this.stopPublishing(event)
			},
			stopVideoSession: (event) => {
				console.debug('stopVideoSession Event')
				this.stopSession(event)
			},
			tokboxError: (error) => {
				this.errorHandler(error)
			},
			browserAudioBlocked: () => {
				this.showAudioBlockedWarning = true
			},
			screenShareStopped: () => {
				this.stopPublishing('screen')
			},
			leaveMeeting: () => {
				console.debug('leaveMeetingEvent')
				this.leaveMeeting()
			},
			lostAudioDevice: () => {
				if (this.lostAudioAlert) {
					hideAlert(this.lostAudioAlert)
				}
				this.lostAudioAlert = showAlert({
					text: this.$t('meetings_live_error_audiodevice'),
					error: true,
					autoHide: false,
					allowClose: true,
				})
			},
			lostVideoDevice: () => {
				if (this.lostVideoAlert) {
					hideAlert(this.lostVideoAlert)
				}
				this.lostVideoAlert = showAlert({
					text: this.$t('meetings_live_error_videodevice'),
					error: true,
					autoHide: false,
					allowClose: true,
				})
			},
			dismissLostAudioAlert: () => {
				if (this.lostAudioAlert) {
					hideAlert(this.lostAudioAlert)
				}
			},
			dismissLostVideoAlert: () => {
				if (this.lostVideoAlert) {
					hideAlert(this.lostVideoAlert)
				}
			},
			browserAudioBlocked: () => {
				this.showAudioBlockedWarning = true
			},
			showOverLimitWarning: () => {
				this.showOverLimitPopup = true
				if (getIsPublishing()) {
					this.stopPublishing('video')
				}
			},
			meetingsDisabled: () => {
				this.errorHandler('MEETINGS_DISABLED')
			},
			openProfileCard: (user) => {
				this.profileData = user || null
			},
			captionsActive: (isActive) => {
				this.isCaptionsActive = isActive
			},
		})

		await this.initState()

		if (screenfull.isEnabled) {
			screenfull.on('change', () => {
				this.fullscreenActive = screenfull.isFullscreen
			})
		}

		window.onfocus = this.focus
		window.onblur = this.blur
		this.focus()

		document.addEventListener('click', this.unblockAudio)
		document.body.classList.add('meetings')

		//subscribe to Pusher presence channel
		subscribeToPresence()
	},

	watch: {
		'store.videosession.status': {
			handler(status) {
				logMeetingEvent('videosession.status changed', status)
				if (status === 'FINISHED' && this.store.session.isConnected()) {
					this.showJoinMeetingPopup = false
					this.tearDownSession()
				}
			},
			deep: true,
		},
		isConnected(connected) {
			if (!connected) return
			const membership = getMyMembership()
			const bubble = getBubble()
			document.title = this.$t('meetings_web_title', {
				GroupTitle: membership.alias || bubble.title,
			})
		},
		meetingEnded(ended) {
			if (ended) {
				setTimeout(() => {
					unsubscribeToPresence()
				}, FIVE_MINS)
			}
		},
	},

	computed: {
		isConnected() {
			return this.isReady && hasConnection()
		},

		notPublishing() {
			return !getIsPublishing()
		},
	},

	methods: {
		focus() {
			mainStore.appVisible = true
		},
		blur() {
			mainStore.appVisible = false
		},
		dragChange(isDragging, e) {
			if (e && e.target) {
				dragTarget = e.target
			}
			EventBus.$emit('meetingsDrag', isDragging)
		},
		dragOff(e) {
			if (dragTarget === e.target) {
				EventBus.$emit('meetingsDrag', false)
			}
		},
		changeView(view) {
			this.view = view
		},

		setSidebarOpen(isOpen) {
			this.isSidebarOpen = isOpen
		},

		startPublishing() {
			this.showJoinMeetingPopup = false
			setPublishing(true)
		},

		preLiveCheck() {
			if (this.store.initializing) {
				//this.preStream()
				// let the server know we're still alive while the user allows proper permissions
				setTimeout(this.preLiveCheck, 5000)
			}
		},

		parseBubbleIdFromRoute() {
			return this.$route.path.replace('/meeting/', '')
		},

		readUserPrefsFromRoute() {
			const { video, audio, hd, language, ...rest } = this.$route.query
			const prefs = {
				video: video === 'true',
				audio: audio === 'true',
				hd: hd !== 'false',
				language,
			}
			setUserPrefs(prefs)
			if (video || audio || hd || language) {
				this.$router.replace({
					path: `/meeting/${this.bubbleId}`,
					query: rest,
				})
			}
		},

		async unblockAudio() {
			if (!this.showAudioBlockedWarning) return
			try {
				await OT.unblockAudio()
				this.showAudioBlockedWarning = false
				document.removeEventListener('click', this.unblockAudio)
			} catch (e) {
				console.error(e)
			}
		},

		async initState() {
			if (!this.bubbleId) return
			try {
				const membership = await fetchMyMembershipForChat(this.bubbleId)
				const chat = normalizeChat(membership.chat)

				store.bubble = chat
				store.user = mainStore.auth.user
				mainStore.currentChatId = chat.id

				//check that meetings are enabled
				const org = getOrganization()
				if (!org.meetings_enabled) {
					this.errorHandler('MEETINGS_DISABLED')
					return
				}

				if (chat.isdm && !chat.isMultiDM) {
					this.changeView(views.ACTIVE_SPEAKER)
				}

				//connect to pusher and listen for specific events we need...
				initPusher()
				subscribeToPusherChannels(this.$pusher)

				// join stream and store videosession data
				const { videosession, apikey, token } = await joinStream()

				Object.assign(this.sessionData, {
					sessionId: videosession.sessionid,
					apiKey: apikey,
					token,
					bubble_id: chat.id,
					videosession_id: videosession.id,
					maxstreams: videosession.maxstreams,
				})
				setVideoSession(videosession)

				this.$nextTick(function () {
					if (videosession.status === 'RESERVED') {
						//call prestream if we're the first one publishing
						preStream()
					}
					this.sessionInit()
				})
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
				this.tearDownSession()
			}
		},

		// Initialize the tokbox session
		// TODO - break this out into it's own Session style handler file?
		sessionInit() {
			const session = OT.initSession(
				this.sessionData.apiKey,
				this.sessionData.sessionId
			)
			setSession(session)

			session.connect(this.sessionData.token, (err) => {
				if (err) {
					return this.errorHandler(err)
				}
			})

			session.on('networkDisconnected', (event) => {
				logMeetingEvent('networkDisconnected Event', event)
				alert('Your network has disconnected')
			})

			session.on('forceDisconnected', (event) => {
				logMeetingEvent('forceDisconnected Event', event)
				alert('Your connection has been disconnected by the moderator')
			})

			session.on('sessionDisconnected', (event) => {
				logMeetingEvent('sessionDisconnected Event', event)
			})

			session.on('clientDisconnected', (event) => {
				logMeetingEvent('sessionDisconnected Event', event)
				alert('Your connection to the session was disconnected')
			})

			session.on('sessionReconnecting', (event) => {
				logMeetingEvent('sessionReconnecting Event...', event)
			})

			session.on('connectionCreated', ({ connection }) => {
				logMeetingEvent('adding connection', connection.id)
				setConnection(connection)
			})

			session.on('connectionDestroyed', ({ connection }) => {
				logMeetingEvent('removing connection = ' + connection.id)
				removeConnection(connection.id)
			})

			session.on('streamCreated', ({ stream }) => {
				stream = setStream(stream)
				EventBus.$emit('streamUpdate', stream)
				if (stream.isScreenShare) {
					EventBus.$emit('screenStream', stream)
				}
			})

			session.on('streamDestroyed', ({ stream }) => {
				removeStream(stream.connection.id, stream.realVideoType)
				EventBus.$emit('streamUpdate')
				EventBus.$emit('streamDestroyed', stream)

				// if we have no streams left and we're not publishing, AND we have fully connected and setup our session we should tear down
				if (!hasSubscribers() && !getIsPublishing() && this.initialized) {
					logMeetingEvent(
						"streamDestroyed - checking for teardown, we have no streams and we're not publishing"
					)
					this.tearDownSession()
				}
			})

			session.on(
				'streamPropertyChanged',
				({ changedProperty, oldValue, newValue, stream }) => {
					logMeetingEvent(
						'streamPropertyChanged',
						`"${changedProperty}"`,
						'changed from',
						oldValue,
						'to',
						newValue,
						'for stream',
						stream?.streamId
					)
					setStream(stream)
					EventBus.$emit('streamUpdate')
				}
			)

			session.on('signal:mute', (event) => {
				// some other user told us to mute
				const publisher = getPublisher()
				const { connectionId } = event.from
				// only mute if we're not currently muted and we were not the ones muting everyone else
				if (
					publisher &&
					publisher.stream.hasAudio &&
					connectionId !== getMyConnectionId()
				) {
					publisher.publishAudio(false)
					let text = 'You were muted'
					if (event.from) {
						const user = getUserByConnectionId(connectionId)
						if (user) {
							text = this.$t('meetings_action_message_someone_muted_you', [
								`${user.firstname} ${user.lastname}`,
							])
						}
					}
					showAlert({ text, icon: MicOffIcon })
					EventBus.$emit('publisherMuted', null)
				}
			})

			OT.on('exception', (event) => {
				console.error('UNKNOWN TOKBOX EXCEPTION', event)
				logMeetingEvent('Exception', 'Attempting to report issue to TokBox...')
				OT.reportIssue((error, issueId) => {
					logMeetingEvent('Successfully reported TokBox issue', issueId)
					this.reportToBugsnag(event, issueId)
				})
			})

			this.ingestExistingSession()
		},

		reportToBugsnag(event = null, issueId = null) {
			const error = new Error('Tokbox exception reported')
			this.$bugsnag.notify(error, (e) => {
				const { user } = mainStore.auth
				const { email } = user.emails?.length ? user.emails[0] : {}
				const { phone } = user.phones?.length ? user.phones[0] : {}
				e.setUser(user.id, email || phone, user.fullname)

				e.context = 'Meeting.vue'
				e.addMetadata('tokboxException', event)
				e.addMetadata('tokboxIssueId', issueId)
			})
		},

		stopPublishing(type) {
			logMeetingEvent('stopping for type = ' + type)
			const session = getSession()
			if (type === 'video' && getIsPublishing()) {
				logMeetingEvent('tearing down video pub')
				setPublishing(false)
				const publisher = getPublisher()
				session.unpublish(publisher)
				logMeetingEvent('unpublished')
				stopStream()
				removePublisher()
				logMeetingEvent('publisher removed')
			} else if (type === 'screen' && getIsSharing()) {
				logMeetingEvent('tearing down screen pub')
				setSharing(false)
				const publisher = getScreenPublisher()
				session.unpublish(publisher)
				removeScreenPublisher()
			}
			EventBus.$emit('streamUpdate')

			// check to see if we're the last one streaming and if so, tear down the session
			if (!hasSubscribers() && !getIsPublishing() && !getIsSharing()) {
				logMeetingEvent(
					"stopPublishing() - we don't have any streams left, so we're going to tear it down"
				)
				this.tearDownSession()
			}
		},

		stopSession() {
			//check to see if we're publishing our video or screen and stop that
			if (getIsPublishing()) {
				this.stopPublishing('video')
			}

			if (getIsSharing()) {
				this.stopPublishing('screen')
			}
			stopVideoSession()
		},

		ingestExistingSession() {
			if (!getMyConnectionId()) {
				// Stupid thing you have to do to give tokbox time to send us the events we need (like one for our own connection - eye roll)
				setTimeout(this.ingestExistingSession, 500)
				return
			}
			const { query } = this.$route
			if (query.golive) {
				// Remove the golive param after we load it so people don't accidentally
				// launch a new meeting by reloading their webpage
				this.$router.replace({
					path: `/meeting/${this.bubbleId}`,
					query: { ...query, golive: undefined },
				})
				if (!isOverLimit()) {
					// we are the first ones here and we want to publish based on the prefs passed in...
					setPublishing(true)
				}
			} else {
				logMeetingEvent('no we are not first')
				// we are not the first ones here... If we're not over the limit on users let's show the join popup
				if (!isOverLimit()) {
					this.showJoinMeetingPopup = true
				}
			}

			this.initialized = true
		},

		tearDownSession() {
			logMeetingEvent('tearing down the session')
			//check to see if we're publishing our video or screen and stop that
			if (getIsPublishing()) {
				this.stopPublishing('video')
			}

			if (getIsSharing()) {
				this.stopPublishing('screen')
			}

			// disconnect from the tokbox session
			this.store.session?.disconnect?.()
			this.store.connectionIds?.forEach((connection_id) => {
				removeConnection(connection_id)
			})

			screenfull.exit()
			this.endReason = 'meeting_ended'
			this.meetingEnded = true
			this.showJoinMeetingPopup = false
		},

		errorHandler(error) {
			logMeetingEvent('error handler', error)
			this.errorData = getErrorText(error)
			this.showError = true
		},

		profileError(error) {
			showAlert({
				text: error,
				error: true,
				time: 4000,
			})
		},

		leaveMeeting() {
			if (getIsPublishing()) {
				this.stopPublishing('video')
			}

			if (getIsSharing()) {
				this.stopPublishing('screen')
			}

			this.store.session.disconnect()
			screenfull.exit()
			this.endReason = 'meeting_left'
			this.meetingEnded = true
		},

		toggleFullscreen() {
			screenfull.toggle(document.querySelector('.pronto-container'))
		},
	},
}
</script>

<style lang="scss" scoped>
$gap: 16px;
$captions-height: 204px;

.meeting-container {
	width: 100vw;
	height: 100vh;
	position: relative;
	background: #111;
	min-width: 960px;
}

.meeting-spinner {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.meeting-layout {
	width: 100%;
	height: 100%;
	padding: $gap;
	display: grid;
	grid-template-columns: 1fr auto;
	grid-template-rows: auto 1fr 0;
	gap: $gap;
	grid-template-areas:
		'header header'
		'main sidebar'
		'captions sidebar';
	outline: 0;
	transition: all ease-in-out 0.3s;

	&.with-captions {
		grid-template-rows: auto 1fr $captions-height;
	}
}

.wrapper {
	border-radius: 10px;
	color: white;
}

.meeting-header {
	grid-area: header;
	@extend .wrapper;
}
.meeting-main {
	grid-area: main;
	overflow: hidden;
}
.meeting-sidebar {
	grid-area: sidebar;
	@extend .wrapper;
}

.connecting-spinner {
	overflow: hidden;
	height: 100%;
	position: absolute;
	top: calc(50% - 50px);
	left: 0;
	right: 0;
	:deep(.ios-spinner) {
		background: #111 !important;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	&.meetings {
		.modal-overlay {
			.modal-content:not(.fullscreen) {
				// basic modals should be "light" in meetings
				background: white !important;
			}
		}
		.standard-modal {
			.modal-overlay {
				.modal-content {
					// newer standard modals should be dark in meetings
					background: var(--neutral-200) !important;
				}
			}
		}
	}
}
</style>
